import React, { useId, useState } from 'react';
import { SCAN_DISCLAIMER, SCAN_DISCLAIMER_SUMMARY } from '../legal/terms';
import { T } from '../designTokens';

interface ScanDisclaimerProps {
  dismissible?: boolean;
  /** One-line collapsed copy. Defaults to the scan-report summary. */
  summary?: string;
  /** Full text revealed by the expander. Defaults to SCAN_DISCLAIMER. */
  details?: string;
}

const STORAGE_KEY = 'privisy_disclaimer_dismissed';

export default function ScanDisclaimer({
  dismissible = false,
  summary = SCAN_DISCLAIMER_SUMMARY,
  details = SCAN_DISCLAIMER,
}: ScanDisclaimerProps) {
  const detailsId = useId();
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!dismissible) return false;
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // localStorage unavailable — just hide in-memory
    }
    setDismissed(true);
  };

  return (
    <div
      role="note"
      aria-label="Legal disclaimer"
      style={{
        backgroundColor: T.warningBg,
        border: `1px solid ${T.warningBorder}`,
        borderLeft: `2px solid ${T.warning}`,
        borderRadius: T.radiusMd,
        padding: '8px 14px',
        marginBottom: '16px',
      }}
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <span
          style={{
            flex: 1,
            fontSize: '13px',
            lineHeight: '1.5',
            color: T.amber800,
          }}
        >
          {summary}
        </span>

        <button
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls={detailsId}
          style={{
            flexShrink: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: T.amber700,
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.5,
            padding: '2px 4px',
            borderRadius: T.radiusSm,
          }}
        >
          Details {expanded ? '▾' : '▸'}
        </button>

        {dismissible && (
          <button
            onClick={handleDismiss}
            aria-label="Dismiss disclaimer"
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.amber700,
              fontSize: '18px',
              lineHeight: 1,
              padding: '2px 4px',
              borderRadius: T.radiusSm,
            }}
          >
            ×
          </button>
        )}
      </div>

      {expanded && (
        <div id={detailsId} style={{ marginTop: '8px' }}>
          {details.split('\n\n').map((paragraph, idx) => (
            <p
              key={idx}
              style={{
                margin: idx === 0 ? '0 0 6px' : '6px 0',
                fontSize: '13px',
                lineHeight: '1.6',
                color: T.amber800,
              }}
            >
              {paragraph}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
